import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import api, { clearAuthToken } from '../api';
import { useAuth } from '../context/AuthContext';
import './ProductReview.css';

const ProductReview = () => {
  const { token, productId } = useParams();
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  
  // Mode: 'token' for invite-based, 'direct' for logged-in user direct submission
  const mode = token ? 'token' : 'direct';
  
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  
  const [product, setProduct] = useState(null);
  const [inviteCustomerEmail, setInviteCustomerEmail] = useState(null);
  const [existingDemographics, setExistingDemographics] = useState(null);
  
  // Form state - Review
  const [overallRating, setOverallRating] = useState(0);
  const [hoverRating, setHoverRating] = useState(0);
  const [writtenReview, setWrittenReview] = useState('');
  const [wouldRecommend, setWouldRecommend] = useState(null);
  const [recommendToWhom, setRecommendToWhom] = useState('');
  const [discoverySource, setDiscoverySource] = useState([]);
  const [favoriteAspects, setFavoriteAspects] = useState([]);
  const [completionStatus, setCompletionStatus] = useState('');
  const [incompleteReason, setIncompleteReason] = useState('');
  const [engagementLags, setEngagementLags] = useState('');
  const [recoveryFromLags, setRecoveryFromLags] = useState('');
  const [keyTakeaways, setKeyTakeaways] = useState('');
  const [confusingConcepts, setConfusingConcepts] = useState('');
  const [areasForExpansion, setAreasForExpansion] = useState('');
  const [isPublic, setIsPublic] = useState(true);
  
  // Form state - Demographics
  const [ageGroup, setAgeGroup] = useState('');
  const [gender, setGender] = useState('');
  const [country, setCountry] = useState('');
  const [city, setCity] = useState('');
  const [occupation, setOccupation] = useState('');
  const [preferredFormats, setPreferredFormats] = useState([]);
  const [consumptionFrequency, setConsumptionFrequency] = useState('');
  const [generalFeedback, setGeneralFeedback] = useState('');

  // Options
  const discoveryOptions = [
    { value: 'website', label: 'Website' },
    { value: 'email', label: 'Email' },
    { value: 'social_media', label: 'Social Media' },
    { value: 'recommendation', label: 'Recommendation' },
    { value: 'other', label: 'Other' },
  ];

  const aspectOptions = [
    { value: 'plot', label: 'Plot/Storyline' },
    { value: 'characters', label: 'Characters' },
    { value: 'writing_style', label: 'Writing Style' },
    { value: 'pacing', label: 'Pacing' },
    { value: 'ending', label: 'Ending' },
    { value: 'themes', label: 'Themes/Message' },
  ];

  const formatOptions = [
    { value: 'ebook', label: 'E-Book' },
    { value: 'audiobook', label: 'Audiobook' },
    { value: 'print', label: 'Print' },
    { value: 'podcast', label: 'Podcast' },
  ];

  useEffect(() => {
    const fetchData = async () => {
      if (mode === 'token') {
        // Token-based invite flow
        try {
          const response = await api.get(`/api/review/${token}/`);
          setProduct(response.data.invite?.product);
          const customerEmail = response.data.invite?.customer_email;
          setInviteCustomerEmail(customerEmail);
          
          // IMPORTANT: If a different user is logged in, clear their session completely
          // This prevents the wrong email being stamped on downloads
          if (user && user.email && customerEmail && 
              user.email.toLowerCase() !== customerEmail.toLowerCase()) {
            console.log('Logging out mismatched user for review invite');
            // Call logout to clear BOTH the session cookie AND localStorage token
            try {
              await logout();
            } catch (e) {
              // Logout API might fail, but we still need to clear local state
              clearAuthToken();
            }
            // Force reload to clear all auth state
            window.location.reload();
            return;
          }
          
          // Pre-fill demographics if they exist
          if (response.data.demographics) {
            const demo = response.data.demographics;
            setExistingDemographics(demo);
            setAgeGroup(demo.age_group || '');
            setGender(demo.gender || '');
            setCountry(demo.country || '');
            setCity(demo.city || '');
            setOccupation(demo.occupation || '');
            setPreferredFormats(demo.preferred_formats || []);
            setConsumptionFrequency(demo.consumption_frequency || '');
            setGeneralFeedback(demo.general_feedback || '');
          }
        } catch (err) {
          setError(err.response?.data?.error || 'Invalid or expired review link');
        } finally {
          setLoading(false);
        }
      } else {
        // Direct product review flow
        if (!user) {
          setError('Please log in to write a review');
          setLoading(false);
          return;
        }
        
        try {
          // Fetch product details
          const productResponse = await api.get(`/api/products/${productId}/`);
          setProduct(productResponse.data);
          
          // Also fetch eligibility which includes demographics
          try {
            const eligibilityResponse = await api.get(`/api/products/${productId}/can-review/`);
            if (eligibilityResponse.data.demographics) {
              const demo = eligibilityResponse.data.demographics;
              setExistingDemographics(demo);
              setAgeGroup(demo.age_group || '');
              setGender(demo.gender || '');
              setCountry(demo.country || '');
              setCity(demo.city || '');
              setOccupation(demo.occupation || '');
              setPreferredFormats(demo.preferred_formats || []);
              setConsumptionFrequency(demo.consumption_frequency || '');
              setGeneralFeedback(demo.general_feedback || '');
            }
          } catch (eligibilityErr) {
            // Eligibility check failed, but we can still show the form
            console.warn('Could not fetch eligibility/demographics:', eligibilityErr);
          }
        } catch (err) {
          setError('Product not found');
        } finally {
          setLoading(false);
        }
      }
    };
    
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, productId, mode, user]);

  const handleCheckboxChange = (value, currentList, setList) => {
    if (currentList.includes(value)) {
      setList(currentList.filter(v => v !== value));
    } else {
      setList([...currentList, value]);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (overallRating === 0) {
      setError('Please select a rating');
      return;
    }
    if (writtenReview.length < 10) {
      setError('Please write at least 10 characters in your review');
      return;
    }
    if (wouldRecommend === null) {
      setError('Please indicate if you would recommend this');
      return;
    }
    
    setSubmitting(true);
    setError('');
    
    try {
      const reviewData = {
        overall_rating: overallRating,
        written_review: writtenReview,
        would_recommend: wouldRecommend,
        recommend_to_whom: recommendToWhom,
        discovery_source: discoverySource,
        favorite_aspects: favoriteAspects,
        completion_status: completionStatus,
        incomplete_reason: incompleteReason,
        engagement_lags: engagementLags,
        recovery_from_lags: recoveryFromLags,
        key_takeaways: keyTakeaways,
        confusing_concepts: confusingConcepts,
        areas_for_expansion: areasForExpansion,
        is_public: isPublic,
        demographics: {
          age_group: ageGroup,
          gender: gender,
          country: country,
          city: city,
          occupation: occupation,
          preferred_formats: preferredFormats,
          consumption_frequency: consumptionFrequency,
          general_feedback: generalFeedback,
        }
      };
      
      // Use different endpoint based on mode
      if (mode === 'token') {
        await api.post(`/api/review/${token}/submit/`, reviewData);
      } else {
        await api.post(`/api/products/${productId}/submit-review/`, reviewData);
      }
      setSuccess(true);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to submit review');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="review-container">
        <div className="review-loading">Loading...</div>
      </div>
    );
  }

  if (error && !product) {
    return (
      <div className="review-container">
        <div className="review-error-page">
          <h2>Unable to Load Review</h2>
          <p>{error}</p>
          <button onClick={() => navigate('/')} className="btn-primary">
            Return Home
          </button>
        </div>
      </div>
    );
  }

  if (success) {
    return (
      <div className="review-container">
        <div className="review-success">
          <h2>🎉 Thank You!</h2>
          <p>Your review has been submitted successfully.</p>
          <p>We really appreciate you taking the time to share your thoughts!</p>
          <button onClick={() => navigate(productId ? `/products/${productId}` : '/products')} className="btn-primary">
            {productId ? 'Back to Product' : 'Browse More Books'}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="review-container">
      <div className="review-header">
        <h1>Share Your Review</h1>
        {inviteCustomerEmail && (
          <div className="review-as-banner">
            Reviewing as: <strong>{inviteCustomerEmail}</strong>
          </div>
        )}
        {product && (
          <div className="review-product-info">
            {product.thumbnail_url && (
              <img 
                src={product.thumbnail_url} 
                alt={product.title}
                className="review-product-thumbnail"
              />
            )}
            <h2>{product.title}</h2>
          </div>
        )}
      </div>

      <form onSubmit={handleSubmit} className="review-form">
        {error && <div className="review-error">{error}</div>}
        
        {/* Section 1: Product Review */}
        <section className="review-section">
          <h3>📖 Your Review</h3>
          
          {/* Star Rating */}
          <div className="form-group">
            <label>Overall Rating *</label>
            <div className="star-rating">
              {[1, 2, 3, 4, 5].map((star) => (
                <span
                  key={star}
                  className={`star ${star <= (hoverRating || overallRating) ? 'filled' : ''}`}
                  onClick={() => setOverallRating(star)}
                  onMouseEnter={() => setHoverRating(star)}
                  onMouseLeave={() => setHoverRating(0)}
                >
                  ★
                </span>
              ))}
              <span className="rating-text">
                {overallRating > 0 && `${overallRating} out of 5`}
              </span>
            </div>
          </div>

          {/* Written Review */}
          <div className="form-group">
            <label>Your Review *</label>
            <textarea
              value={writtenReview}
              onChange={(e) => setWrittenReview(e.target.value)}
              placeholder="Share your thoughts about this book..."
              rows={6}
              minLength={10}
              maxLength={5000}
            />
            <span className="char-count">{writtenReview.length}/5000</span>
          </div>

          {/* Would Recommend */}
          <div className="form-group">
            <label>Would you recommend this book? *</label>
            <div className="radio-group">
              <label className={`radio-option ${wouldRecommend === true ? 'selected' : ''}`}>
                <input
                  type="radio"
                  name="recommend"
                  checked={wouldRecommend === true}
                  onChange={() => setWouldRecommend(true)}
                />
                Yes
              </label>
              <label className={`radio-option ${wouldRecommend === false ? 'selected' : ''}`}>
                <input
                  type="radio"
                  name="recommend"
                  checked={wouldRecommend === false}
                  onChange={() => setWouldRecommend(false)}
                />
                No
              </label>
            </div>
          </div>

          {wouldRecommend && (
            <div className="form-group">
              <label>Who would you recommend this to?</label>
              <input
                type="text"
                value={recommendToWhom}
                onChange={(e) => setRecommendToWhom(e.target.value)}
                placeholder="e.g., Fans of spiritual growth, people seeking..."
              />
            </div>
          )}

          {/* Favorite Aspects */}
          <div className="form-group">
            <label>What did you enjoy most? (select all that apply)</label>
            <div className="checkbox-group">
              {aspectOptions.map(option => (
                <label key={option.value} className={`checkbox-option ${favoriteAspects.includes(option.value) ? 'selected' : ''}`}>
                  <input
                    type="checkbox"
                    checked={favoriteAspects.includes(option.value)}
                    onChange={() => handleCheckboxChange(option.value, favoriteAspects, setFavoriteAspects)}
                  />
                  {option.label}
                </label>
              ))}
            </div>
          </div>

          {/* Discovery Source */}
          <div className="form-group">
            <label>How did you discover this book?</label>
            <div className="checkbox-group">
              {discoveryOptions.map(option => (
                <label key={option.value} className={`checkbox-option ${discoverySource.includes(option.value) ? 'selected' : ''}`}>
                  <input
                    type="checkbox"
                    checked={discoverySource.includes(option.value)}
                    onChange={() => handleCheckboxChange(option.value, discoverySource, setDiscoverySource)}
                  />
                  {option.label}
                </label>
              ))}
            </div>
          </div>

          {/* Completion Status */}
          <div className="form-group">
            <label>Did you finish reading?</label>
            <select value={completionStatus} onChange={(e) => setCompletionStatus(e.target.value)}>
              <option value="">Select...</option>
              <option value="fully_completed">Yes, fully completed</option>
              <option value="partially">Partially</option>
              <option value="not_finished">Not finished</option>
            </select>
          </div>

          {(completionStatus === 'partially' || completionStatus === 'not_finished') && (
            <div className="form-group">
              <label>What prevented you from finishing?</label>
              <textarea
                value={incompleteReason}
                onChange={(e) => setIncompleteReason(e.target.value)}
                placeholder="Optional: Share what stopped you..."
                rows={3}
              />
            </div>
          )}
        </section>

        {/* Section 2: Deep Feedback (Collapsible) */}
        <section className="review-section collapsible">
          <details>
            <summary><h3>💭 Detailed Feedback (Optional)</h3></summary>
            
            <div className="form-group">
              <label>Did the book lag or lose your interest at any point?</label>
              <textarea
                value={engagementLags}
                onChange={(e) => setEngagementLags(e.target.value)}
                placeholder="If so, where did you notice that?"
                rows={3}
              />
            </div>

            <div className="form-group">
              <label>Did it recover later?</label>
              <textarea
                value={recoveryFromLags}
                onChange={(e) => setRecoveryFromLags(e.target.value)}
                placeholder="Did the book pick back up?"
                rows={3}
              />
            </div>

            <div className="form-group">
              <label>What are your key takeaways?</label>
              <textarea
                value={keyTakeaways}
                onChange={(e) => setKeyTakeaways(e.target.value)}
                placeholder="What will you remember or apply from this book?"
                rows={3}
              />
            </div>

            <div className="form-group">
              <label>Were any concepts confusing?</label>
              <textarea
                value={confusingConcepts}
                onChange={(e) => setConfusingConcepts(e.target.value)}
                placeholder="Anything that needed more clarity?"
                rows={3}
              />
            </div>

            <div className="form-group">
              <label>What would you like to see expanded?</label>
              <textarea
                value={areasForExpansion}
                onChange={(e) => setAreasForExpansion(e.target.value)}
                placeholder="Topics you'd like explored further?"
                rows={3}
              />
            </div>
          </details>
        </section>

        {/* Section 3: Demographics (Collapsible) */}
        <section className="review-section collapsible">
          <details open={!existingDemographics}>
            <summary>
              <h3>👤 About You (Optional)</h3>
              {existingDemographics && <span className="prefilled-badge">Pre-filled from profile</span>}
            </summary>
            
            <div className="demographics-grid">
              <div className="form-group">
                <label>Age Group</label>
                <select value={ageGroup} onChange={(e) => setAgeGroup(e.target.value)}>
                  <option value="">Prefer not to say</option>
                  <option value="18-24">18-24</option>
                  <option value="25-34">25-34</option>
                  <option value="35-44">35-44</option>
                  <option value="45-54">45-54</option>
                  <option value="55-64">55-64</option>
                  <option value="65+">65+</option>
                </select>
              </div>

              <div className="form-group">
                <label>Gender</label>
                <select value={gender} onChange={(e) => setGender(e.target.value)}>
                  <option value="">Prefer not to say</option>
                  <option value="male">Male</option>
                  <option value="female">Female</option>
                </select>
              </div>

              <div className="form-group">
                <label>Country</label>
                <input
                  type="text"
                  value={country}
                  onChange={(e) => setCountry(e.target.value)}
                  placeholder="Your country"
                />
              </div>

              <div className="form-group">
                <label>City</label>
                <input
                  type="text"
                  value={city}
                  onChange={(e) => setCity(e.target.value)}
                  placeholder="Your city"
                />
              </div>

              <div className="form-group">
                <label>Occupation</label>
                <select value={occupation} onChange={(e) => setOccupation(e.target.value)}>
                  <option value="">Prefer not to say</option>
                  <option value="student">Student</option>
                  <option value="professional">Professional</option>
                  <option value="creative">Creative</option>
                  <option value="retired">Retired</option>
                  <option value="other">Other</option>
                </select>
              </div>

              <div className="form-group">
                <label>Reading Frequency</label>
                <select value={consumptionFrequency} onChange={(e) => setConsumptionFrequency(e.target.value)}>
                  <option value="">Select...</option>
                  <option value="daily">Daily</option>
                  <option value="weekly">Weekly</option>
                  <option value="monthly">Monthly</option>
                  <option value="occasionally">Occasionally</option>
                </select>
              </div>
            </div>

            <div className="form-group">
              <label>Preferred Formats</label>
              <div className="checkbox-group">
                {formatOptions.map(option => (
                  <label key={option.value} className={`checkbox-option ${preferredFormats.includes(option.value) ? 'selected' : ''}`}>
                    <input
                      type="checkbox"
                      checked={preferredFormats.includes(option.value)}
                      onChange={() => handleCheckboxChange(option.value, preferredFormats, setPreferredFormats)}
                    />
                    {option.label}
                  </label>
                ))}
              </div>
            </div>

            <div className="form-group">
              <label>Any other feedback for Christensen Kirtis Publishing?</label>
              <textarea
                value={generalFeedback}
                onChange={(e) => setGeneralFeedback(e.target.value)}
                placeholder="Suggestions, ideas, or anything else you'd like to share..."
                rows={3}
              />
            </div>
          </details>
        </section>

        {/* Privacy & Submit */}
        <section className="review-section submit-section">
          <label className="checkbox-option privacy-checkbox">
            <input
              type="checkbox"
              checked={isPublic}
              onChange={(e) => setIsPublic(e.target.checked)}
            />
            Allow my review to be displayed publicly (your email will not be shown)
          </label>

          <button type="submit" className="btn-submit" disabled={submitting}>
            {submitting ? 'Submitting...' : 'Submit Review'}
          </button>
        </section>
      </form>
    </div>
  );
};

export default ProductReview;
